import { NewslettersubcribeApiService } from "./services/newslettersubcribe-api.service";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { FeaturedbrandApiService } from "./services/featuredbrand-api.service";
import { FeaturedBrand } from "./models/featuredbrand-interface";
import {
  ApiResponse,
  FetchResponse,
} from "src/app/shared/models/api-response.interface";
import { ActivatedRoute, Router } from "@angular/router";
import { TopCategoriesApiService } from "./services/topcategories-api.service";
import { TopCategories } from "./models/topcategories-interface";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PopularProductApiService } from "./services/popularproduct-api.service";
import { ProductResponse } from "../product-list/models/product-interface";
import { Category } from "../product-list/models/category-interface";
import { CategoryApiService } from "../product-list/services/category-api.service";
import { ProductApiService } from "../product-list/services/product-api.service";
import { WishlistApiService } from "src/app/shared/services/wishlist-api.service";
import { AuthService } from "../auth/services/auth.service";
import { CalculationDialogComponent } from "src/app/shared/components/calculation-dialog/calculation-dialog.component";
import { MeasurementsApiService } from "../measurements/services/measurements-api.service";
import { MatDialog } from "@angular/material/dialog";
import { SnackbarComponent } from "src/app/shared/components/snackbar/snackbar.component";
import { SystemHealthService } from "src/app/shared/services/system-health.service";
import { TranslateService } from "@ngx-translate/core";

export type HomeFlowMode = "shop" | "custom";

export interface AudienceFilterOption {
  id: string | null;
  label: string;
  isTranslationKey: boolean;
}

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrl: "./home.component.scss",
  standalone: false,
})
export class HomeComponent implements OnInit, OnDestroy {
  subscription: Subscription = new Subscription();
  featuredBrand!: FetchResponse<FeaturedBrand[]>;
  topCategories!: FetchResponse<TopCategories[]>;
  shopCategories!: FetchResponse<TopCategories[]>;
  allShopCategories: Category[] = [];
  uniformCategory: Category | null = null;

  activeMode: HomeFlowMode = "custom";
  hasVisitedCustom = true;
  selectedAudienceId: string | null = null;
  audienceOptions: AudienceFilterOption[] = this.getDefaultAudienceOptions();

  popularProduct!: ProductResponse[];
  shopProducts: ProductResponse[] = [];
  shopProductsLoading = false;
  popularProductsError = false;
  wishlistIds: string[] = [];
  pageSize = 9999;
  pageIndex = 0;
  isDegraded = false;
  private audienceProductSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private __snackBar: MatSnackBar,
    private featuredBrandApiService: FeaturedbrandApiService,
    private topCategoriesApiService: TopCategoriesApiService,
    private categoryApiService: CategoryApiService,
    private productApiService: ProductApiService,
    private newslettersubcribeApiService: NewslettersubcribeApiService,
    private popularProductApiService: PopularProductApiService,
    private wishlistService: WishlistApiService,
    private authService: AuthService,
    private measurementApiService: MeasurementsApiService,
    private matDialog: MatDialog,
    private translate: TranslateService,
    private systemHealth: SystemHealthService
  ) {}

  ngOnInit(): void {
    this.isDegraded = this.systemHealth.isDown();

    const requestedMode = this.router.getCurrentNavigation()?.extras.state?.['homeMode'] ?? history.state?.['homeMode'];
    if (requestedMode === 'custom' || requestedMode === 'shop') {
      this.activeMode = requestedMode;
      this.hasVisitedCustom = requestedMode === 'custom';
    }

    if (this.isDegraded) {
      return;
    }

    const loginSuccess = localStorage.getItem("login_success") === "true";
    const isFirstLogin =
      localStorage.getItem("firstLogin") === localStorage.getItem("lastLogin");

    this.fetchShopCategories();

    this.subscription.add(
      this.route.queryParams.subscribe((params) => {
        this.pageIndex = params["pageIndex"] || 0;
        this.pageSize = params["pageSize"] || 9999;
        if (params["mode"] === "custom") {
          this.activeMode = "custom";
          this.hasVisitedCustom = true;
        } else if (params["mode"] === "shop") {
          this.activeMode = "shop";
        }
        this.fetchTopCategories();
      })
    );

    this.subscription.add(
      this.authService.isLoggedIn().subscribe((loggedIn) => {
        if (!loggedIn) {
          this.wishlistIds = [];
          this.wishlistService.updateWishlistState([]);
        }
      })
    );

    this.subscription.add(
      this.wishlistService.wishlist$.subscribe((ids) => {
        this.wishlistIds = ids;
      })
    );

    if (!loginSuccess) return;

    this.showLoginSnackbar(isFirstLogin);
    this.clearLoginFlags();
    this.loadMeasurementStatus();

    if (isFirstLogin) {
      this.router.navigate(["/profiles"], {
        queryParams: { tab: "measurement" },
      });
    }
  }

  ngOnDestroy(): void {
    this.audienceProductSub?.unsubscribe();
    this.subscription.unsubscribe();
  }

  setMode(mode: HomeFlowMode): void {
    if (this.activeMode === mode) {
      return;
    }
    this.activeMode = mode;
    if (mode === "custom") {
      this.hasVisitedCustom = true;
      if (!this.topCategories) {
        this.fetchTopCategories();
      }
    } else if (mode === "shop" && !this.popularProduct && !this.shopProductsLoading) {
      this.fetchPopularProducts();
    }
  }

  selectAudience(audienceId: string | null): void {
    this.router.navigate(['/product'], {
      queryParams: audienceId
        ? { category: audienceId, pageIndex: 0 }
        : { pageIndex: 0 },
    });
  }

  onUniformExplore(): void {
    if (this.uniformCategory?.id) {
      this.router.navigate(["/product"], {
        queryParams: { category: this.uniformCategory.id, pageIndex: 0 },
      });
      return;
    }
    this.router.navigate(["/product"], {
      queryParams: { personalize: true, pageIndex: 0 },
    });
  }

  get shopViewAllQueryParams(): Record<string, string | number | boolean> {
    if (this.selectedAudienceId && this.allShopCategories.some((c) => c.id === this.selectedAudienceId)) {
      return { category: this.selectedAudienceId, pageIndex: 0 };
    }
    return { pageIndex: 0 };
  }

  get activeShopCategoryId(): string | null {
    if (this.selectedAudienceId && this.allShopCategories.some((c) => c.id === this.selectedAudienceId)) {
      return this.selectedAudienceId;
    }
    return null;
  }

  fetchShopCategories(): void {
    this.subscription.add(
      this.categoryApiService.fetchCategoriesWithSub(false).subscribe({
        next: (res: ApiResponse<Category[]>) => {
          this.allShopCategories = (res?.data ?? []).filter(
            (category) => !this.isPersonalizeCategory(category)
          );
          this.uniformCategory =
            this.allShopCategories.find((cat) => this.isUniformOrGroupCategory(cat)) ?? null;
          this.audienceOptions = this.buildAudienceOptions(this.allShopCategories);
          this.updateShopCategoriesView();
        },
        error: () => {
          this.allShopCategories = [];
          this.audienceOptions = this.getDefaultAudienceOptions();
          this.updateShopCategoriesView();
        },
      })
    );
  }

  private getDefaultAudienceOptions(): AudienceFilterOption[] {
    return [
      { id: null, label: "home.shop.audience_all", isTranslationKey: true },
    ];
  }

  private isUniformOrGroupCategory(cat: Category): boolean {
    const text = `${cat.name ?? ""} ${cat.nameCode ?? ""}`.toLowerCase();
    return /đồng phục|uniform|gia đình|cặp|nhóm|team|family|couple/.test(text);
  }

  private isPersonalizeCategory(cat: Category): boolean {
    const code = (cat.nameCode ?? "").trim().toUpperCase();
    const name = (cat.name ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
    return code === "CNH" || code === "PERSONALIZATION" ||
      /ca nhan hoa|personaliz|personalis/.test(name);
  }

  private hasCategoryContent(cat: Category): boolean {
    return (cat.subCategories?.length ?? 0) > 0 || (cat.totalProducts ?? 0) > 0;
  }

  private formatAudienceLabel(name: string): string {
    const trimmed = (name ?? "").trim();
    if (!trimmed) return "";
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  }

  private buildAudienceOptions(categories: Category[]): AudienceFilterOption[] {
    const shopGroupCategories = categories.filter(
      (cat) =>
        !this.isUniformOrGroupCategory(cat) &&
        !this.isPersonalizeCategory(cat) &&
        this.hasCategoryContent(cat)
    );
    const fallbackCategories = categories.filter(
      (cat) => !this.isUniformOrGroupCategory(cat) && this.hasCategoryContent(cat)
    );
    const source = (shopGroupCategories.length > 0 ? shopGroupCategories : fallbackCategories)
      .slice()
      .sort((a: any, b: any) => {
        const aHasProd = (a.totalProducts ?? 0) > 0 ? 1 : 0;
        const bHasProd = (b.totalProducts ?? 0) > 0 ? 1 : 0;
        if (bHasProd !== aHasProd) return bHasProd - aHasProd;
        return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      });

    if (source.length === 0) {
      return this.getDefaultAudienceOptions();
    }

    return [
      { id: null, label: "home.shop.audience_all", isTranslationKey: true },
      ...source.map((cat) => ({
        id: cat.id,
        label: this.formatAudienceLabel(cat.name),
        isTranslationKey: false,
      })),
    ];
  }

  private updateShopCategoriesView(): void {
    const selectedCat = this.selectedAudienceId
      ? this.allShopCategories.find((c) => c.id === this.selectedAudienceId)
      : null;

    const sourceCategories = selectedCat
      ? [selectedCat]
      : this.allShopCategories.filter((c) => !this.isUniformOrGroupCategory(c));

    const effectiveCategories =
      sourceCategories.length > 0 ? sourceCategories : this.allShopCategories;

    const rawSubCategories = effectiveCategories.flatMap((category) => {
      const subs = category.subCategories ?? [];
      if (subs.length > 0) {
        return subs.map((sub) => ({
          id: sub.id,
          categoryId: category.id,
          name: sub.name,
          description: sub.description ?? "",
          imageUrl: sub.imageUrl ?? "",
          totalProducts: sub.productCount ?? 0,
          displayOrder: sub.displayOrder ?? 0,
        }));
      }
      return [
        {
          id: category.id,
          categoryId: category.id,
          name: category.name,
          description: category.description ?? "",
          imageUrl: category.imageUrl ?? "",
          totalProducts: category.totalProducts ?? 0,
          displayOrder: 0,
        },
      ];
    });

    const withProducts = rawSubCategories.filter((item) => item.totalProducts > 0);
    const baseItems = withProducts.length > 0 ? withProducts : rawSubCategories;

    const sorted = baseItems
      .slice()
      .sort(
        (a, b) =>
          b.totalProducts - a.totalProducts ||
          a.displayOrder - b.displayOrder ||
          a.id.localeCompare(b.id)
      );

    const items: TopCategories[] = sorted.map(({ displayOrder, ...rest }) => rest);
    const totalCount = items.length;

    this.shopCategories = {
      pageIndex: 0,
      pageSize: totalCount || 1,
      totalCount,
      totalPages: 1,
      items,
    };
  }

  private showLoginSnackbar(isFirstLogin: boolean) {
    if (!isFirstLogin) return;

    this.translate.get("home.snackbars.welcome_measurement").subscribe((msg) => {
      this.__snackBar.openFromComponent(SnackbarComponent, {
        data: {
          message: msg,
          type: "success",
        },
        duration: 6000,
        horizontalPosition: "right",
        verticalPosition: "top",
        panelClass: ["custom-snackbar"],
      });
    });
  }

  private clearLoginFlags() {
    localStorage.removeItem("login_success");
    localStorage.removeItem("firstLogin");
    localStorage.removeItem("lastLogin");
  }

  retry() {
    this.systemHealth.clear();
    location.reload();
  }

  private loadMeasurementStatus() {
    this.measurementApiService.fetchMeasurementData().subscribe({
      next: (res) => {
        const data = res?.data;
        if (!data || this.hasNullFields(data)) {
          this.openMeasurementDialog();
        }
      },
      error: () => {
        this.openMeasurementDialog();
      },
    });
  }

  private hasNullFields(data: any): boolean {
    return Object.keys(data).some(
      (key) =>
        key !== "id" &&
        key !== "userId" &&
        (data[key] === null || data[key] === undefined)
    );
  }

  private openMeasurementDialog() {
    this.matDialog.open(CalculationDialogComponent, {
      width: "480px",
      maxWidth: "90vw",
      maxHeight: "90vh",
      panelClass: "calculation-dialog-panel",
      data: {
        title: this.translate.instant("home.dialogs.measurement_title"),
        content: this.translate.instant("home.dialogs.measurement_content"),
      },
    });
  }

  private fetchWishlist(): void {
    this.wishlistService.getWishlist(this.pageSize, this.pageIndex).subscribe({
      next: (res) => {
        if (res?.success && res.data) {
          const ids = res.data.items.map((item: any) => item.product.id);
          this.wishlistIds = ids;
          this.wishlistService.updateWishlistState(ids);
        }
      },
      error: (err) => {
        if (err.status === 401) {
          this.wishlistIds = [];
          this.wishlistService.updateWishlistState([]);
        }
      },
    });
  }

  onWishlistUpdated(): void {
    this.fetchWishlist();
  }

  fetchFeaturedBrands() {
    this.subscription.add(
      this.featuredBrandApiService.fetchFeturedBrands().subscribe({
        next: (res: ApiResponse<FetchResponse<FeaturedBrand[]>>) => {
          this.featuredBrand = res.data;
        },
      })
    );
  }

  fetchTopCategories() {
    this.subscription.add(
      this.topCategoriesApiService.fetchTopCategories().subscribe({
        next: (res: ApiResponse<FetchResponse<TopCategories[]>>) => {
          this.topCategories = res.data;
        },
      })
    );
  }

  onShopProductsInViewport(): void {
    if (this.popularProduct || this.shopProductsLoading || this.selectedAudienceId || this.popularProductsError) {
      return;
    }
    this.fetchPopularProducts();
  }

  fetchPopularProducts() {
    this.popularProductsError = false;
    this.shopProductsLoading = true;
    this.subscription.add(
      this.popularProductApiService.fetchPopularProduct().subscribe({
        next: (res: ApiResponse<FetchResponse<ProductResponse[]>>) => {
          this.popularProduct = (res?.data?.items ?? []).slice(0, 18);
          if (!this.selectedAudienceId) {
            this.shopProducts = this.popularProduct;
          }
          this.shopProductsLoading = false;
        },
        error: () => {
          this.popularProductsError = true;
          this.shopProductsLoading = false;
        },
      })
    );
  }

  private loadAudienceProducts(categoryId: string): void {
    this.audienceProductSub?.unsubscribe();
    this.popularProductsError = false;
    this.shopProductsLoading = true;
    this.audienceProductSub = this.productApiService
      .fetchProductsByCategory(categoryId, 18, 0)
      .subscribe({
        next: (res: ApiResponse<FetchResponse<ProductResponse[]>>) => {
          this.shopProducts = (res?.data?.items ?? []).slice(0, 18);
          this.shopProductsLoading = false;
        },
        error: () => {
          this.popularProductsError = true;
          this.shopProductsLoading = false;
        },
      });
  }

  retryShopProducts(): void {
    if (this.selectedAudienceId && this.allShopCategories.some((c) => c.id === this.selectedAudienceId)) {
      this.loadAudienceProducts(this.selectedAudienceId);
      return;
    }
    this.fetchPopularProducts();
  }

  onSubscribe(email: string) {
    this.subscription.add(
      this.newslettersubcribeApiService.subscribeNewsletter(email).subscribe({
        next: () => {
          this.__snackBar.openFromComponent(SnackbarComponent, {
            data: {
              message: this.translate.instant("home.snackbars.subscribe_success"),
              type: "success",
            },
            duration: 6000,
            horizontalPosition: "right",
            verticalPosition: "top",
            panelClass: ["custom-snackbar"],
          });
        },
        error: () => {},
      })
    );
  }
}
